import { ApiError, uuid } from "@gjwtrack/client";
import { DASHBOARD_COLS, DASHBOARD_ROW_PX, type Dashboard, type DashboardAccess, type DashboardSummary, type DashboardWidget, type DashboardWidgetType } from "@gjwtrack/domain";
import { ChevronDown, MoreHorizontal, Pencil, Plus } from "lucide-react";
import GridLayout, { useContainerWidth, type Layout } from "react-grid-layout";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useNavigate, useParams } from "react-router";
import { DashboardShareDialog } from "../components/DashboardShareDialog.tsx";
import { TopBar } from "../components/TopBar.tsx";
import { defaultTitle, WidgetActions, WidgetBody, WidgetFrame } from "../components/widgets/Widgets.tsx";
import { newWidget, WIDGET_TYPES, WidgetSettingsDialog } from "../components/widgets/WidgetSettingsDialog.tsx";
import { client, useClient } from "../lib/client.ts";
import { userName } from "../lib/format.ts";
import { Menu } from "../ui/Menu.tsx";
import { alertService, Button, ListType, Select, SelectType, type SelectItem } from "../ui/ring.ts";

const LAST_KEY = "gjw.dashboard";
const remember = (id: number): void => {
  try {
    localStorage.setItem(LAST_KEY, String(id));
  } catch {
    /* ignore */
  }
};

/** `/dashboards`: go to the last opened dashboard, else the first one (the server seeds "My Dashboard"). */
export function DashboardsIndex() {
  const navigate = useNavigate();
  const [empty, setEmpty] = useState(false);
  useEffect(() => {
    client.http
      .dashboards()
      .then((list) => {
        let last: number | null = null;
        try {
          last = Number(localStorage.getItem(LAST_KEY)) || null;
        } catch {
          /* ignore */
        }
        const pick = list.find((d) => d.id === last) ?? list[0];
        if (pick) navigate(`/dashboard/${pick.id}`, { replace: true });
        else setEmpty(true);
      })
      .catch((e) => client.pushError("dashboards", (e as Error).message));
  }, [navigate]);
  if (!empty) return <div className="flex h-full items-center justify-center text-muted">Loading…</div>;
  return <NoDashboards />;
}

function NoDashboards() {
  const navigate = useNavigate();
  return (
    <div className="flex h-full flex-col">
      <TopBar crumbs={[{ label: "Dashboards" }]} query="" onQuery={(q) => navigate(q ? `/issues?q=${encodeURIComponent(q)}` : "/issues")} onCreate={() => navigate("/issues")} />
      <div className="flex flex-1 flex-col items-center justify-center gap-3 text-muted">
        <div>No dashboards yet.</div>
        <Button primary icon={Plus} onClick={() => void createDashboard(navigate)}>
          New dashboard
        </Button>
      </div>
    </div>
  );
}

async function createDashboard(navigate: (to: string) => void): Promise<void> {
  const name = prompt("Dashboard name", "New dashboard");
  if (!name?.trim()) return;
  try {
    const d = await client.http.command<Dashboard>("createDashboard", { name: name.trim() }, uuid());
    navigate(`/dashboard/${d.id}`);
  } catch (e) {
    client.pushError("dashboard", (e as Error).message);
  }
}

/** `/dashboard/:id`: the grid. Widgets subscribe through this tab's socket, so what they show is what this user may see. */
export function DashboardPage() {
  const { id: idParam } = useParams();
  const id = Number(idParam);
  const navigate = useNavigate();
  const me = useClient((s) => s.me);
  const schema = useClient((s) => s.schema);
  const [dash, setDash] = useState<Dashboard | null>(null);
  const [list, setList] = useState<DashboardSummary[]>([]);
  const [error, setError] = useState<string | null>(null);
  const [editing, setEditing] = useState(false);
  const [settings, setSettings] = useState<{ widget: DashboardWidget; isNew: boolean } | null>(null);
  const [sharing, setSharing] = useState(false);
  const { width, containerRef, mounted } = useContainerWidth();

  const load = useCallback(async (): Promise<void> => {
    try {
      const [d, l] = await Promise.all([client.http.dashboard(id), client.http.dashboards()]);
      setDash(d);
      setList(l);
      setError(null);
      remember(d.id);
    } catch (e) {
      setError(e instanceof ApiError && e.status === 404 ? "This dashboard does not exist or is not shared with you." : (e as Error).message);
    }
  }, [id]);
  useEffect(() => {
    setDash(null);
    setEditing(false);
    void load();
  }, [load]);

  // Saves go whole-dashboard under the optimistic lock; layout drags are debounced.
  const saveTimer = useRef<ReturnType<typeof setTimeout> | null>(null);
  const save = useCallback(
    async (patch: { name?: string; widgets?: DashboardWidget[]; access?: DashboardAccess }, base?: Dashboard): Promise<void> => {
      const cur = base ?? dash;
      if (!cur) return;
      try {
        const next = await client.http.command<Dashboard>("updateDashboard", { dashboardId: cur.id, expectedVersion: cur.version, ...patch }, uuid());
        setDash(next);
        if (patch.name !== undefined || patch.access !== undefined) setList(await client.http.dashboards());
      } catch (e) {
        if (e instanceof ApiError && e.status === 409) {
          alertService.warning("Someone else changed this dashboard; reloaded.");
          await load();
        } else client.pushError("dashboard", (e as Error).message);
      }
    },
    [dash, load],
  );
  const setWidgets = (widgets: DashboardWidget[], immediate = false): void => {
    setDash((d) => (d ? { ...d, widgets } : d));
    if (saveTimer.current) clearTimeout(saveTimer.current);
    const run = (): void => {
      saveTimer.current = null;
      setDash((d) => {
        if (d) void save({ widgets: d.widgets }, d);
        return d;
      });
    };
    if (immediate) run();
    else saveTimer.current = setTimeout(run, 800);
  };
  useEffect(
    () => () => {
      if (saveTimer.current) clearTimeout(saveTimer.current);
    },
    [],
  );

  const layout: Layout = useMemo(() => (dash?.widgets ?? []).map((w) => ({ i: w.id, x: w.x, y: w.y, w: w.w, h: w.h, minW: 3, minH: 2, maxH: 12 })), [dash?.widgets]);
  const onLayoutChange = (next: Layout): void => {
    if (!editing || !dash) return;
    const by = new Map(next.map((l) => [l.i, l]));
    let changed = false;
    const widgets = dash.widgets.map((w) => {
      const l = by.get(w.id);
      if (!l || (l.x === w.x && l.y === w.y && l.w === w.w && l.h === w.h)) return w;
      changed = true;
      return { ...w, x: l.x, y: l.y, w: l.w, h: l.h };
    });
    if (changed) setWidgets(widgets);
  };

  const addWidget = (type: DashboardWidgetType): void => {
    if (!dash) return;
    const def = WIDGET_TYPES.find((t) => t.type === type)!;
    const bottom = dash.widgets.reduce((m, w) => Math.max(m, w.y + w.h), 0);
    setSettings({ widget: newWidget(type, { x: 0, y: bottom, w: def.w, h: def.h }), isNew: true });
    setEditing(true);
  };
  const saveSettings = (w: DashboardWidget): void => {
    if (!dash) return;
    const exists = dash.widgets.some((x) => x.id === w.id);
    setWidgets(exists ? dash.widgets.map((x) => (x.id === w.id ? w : x)) : [...dash.widgets, w], true);
    setSettings(null);
  };
  const removeWidget = (w: DashboardWidget): void => {
    if (!dash || !confirm(`Remove the "${defaultTitle(w)}" widget?`)) return;
    setWidgets(
      dash.widgets.filter((x) => x.id !== w.id),
      true,
    );
  };
  const rename = (): void => {
    if (!dash) return;
    const name = prompt("Dashboard name", dash.name);
    if (name?.trim() && name.trim() !== dash.name) void save({ name: name.trim() });
  };
  const remove = async (): Promise<void> => {
    if (!dash || !confirm(`Delete dashboard "${dash.name}"? This cannot be undone.`)) return;
    try {
      await client.http.command("deleteDashboard", { dashboardId: dash.id }, uuid());
      navigate("/dashboards");
    } catch (e) {
      client.pushError("dashboard", (e as Error).message);
    }
  };

  const mine = list.filter((d) => d.ownerId === me?.id);
  const shared = list.filter((d) => d.ownerId !== me?.id);
  const pickerData: SelectItem[] = [
    ...(mine.length ? [{ key: "t:mine", rgItemType: ListType.TITLE, label: "My dashboards" }] : []),
    ...mine.map((d) => ({ key: d.id, label: d.name })),
    ...(shared.length ? [{ key: "t:shared", rgItemType: ListType.TITLE, label: "Shared with me" }] : []),
    ...shared.map((d) => ({ key: d.id, label: d.name, description: userName(schema, d.ownerId) })),
  ];

  const topBar = <TopBar crumbs={[{ label: "Dashboards", to: "/dashboards" }, ...(dash ? [{ label: dash.name }] : [])]} query="" onQuery={(q) => navigate(q ? `/issues?q=${encodeURIComponent(q)}` : "/issues")} onCreate={() => navigate("/issues")} />;
  if (error) {
    return (
      <div className="flex h-full flex-col">
        {topBar}
        <div className="flex flex-1 items-center justify-center text-muted">{error}</div>
      </div>
    );
  }
  return (
    <div className="flex h-full flex-col">
      {topBar}
      <div className="flex shrink-0 items-center gap-2 border-b border-line px-6 py-2">
        <Select
          type={SelectType.CUSTOM}
          data={pickerData}
          selected={pickerData.find((d) => d.key === id) ?? null}
          filter={list.length > 8}
          onChange={(item: SelectItem | null) => item && typeof item.key === "number" && navigate(`/dashboard/${item.key}`)}
          customAnchor={({ wrapperProps, buttonProps, popup }) => (
            <span {...wrapperProps} className="relative inline-flex">
              <button type="button" id={buttonProps.id} onClick={buttonProps.onClick} data-test={buttonProps["data-test"]} className="flex items-center gap-1 text-xl font-semibold tracking-tight hover:text-value">
                {dash?.name ?? "…"}
                <ChevronDown size={18} className="text-muted" />
              </button>
              {popup}
            </span>
          )}
        />
        {dash && !dash.isOwner && <span className="text-xs text-muted">shared by {userName(schema, dash.ownerId)}</span>}
        <div className="flex-1" />
        {dash?.canEdit && (
          <>
            <Menu anchor={<Button icon={Plus}>Add widget</Button>} items={WIDGET_TYPES.map((t) => ({ key: t.type, label: t.label, onClick: () => addWidget(t.type) }))} />
            <Button icon={Pencil} active={editing} onClick={() => setEditing((e) => !e)}>
              {editing ? "Done" : "Edit"}
            </Button>
          </>
        )}
        <Menu
          anchor={<Button icon={MoreHorizontal} title="More" />}
          items={[
            { key: "new", label: "New dashboard…", onClick: () => void createDashboard(navigate) },
            ...(dash?.canEdit ? [{ key: "rename", label: "Rename…", onClick: rename }] : []),
            ...(dash?.isOwner ? [{ key: "share", label: "Share…", onClick: () => setSharing(true) }, { key: "delete", label: "Delete…", separator: true, onClick: () => void remove() }] : []),
          ]}
        />
      </div>
      <div ref={containerRef} className={`min-h-0 flex-1 overflow-auto px-4 py-3 ${editing ? "dashboard-editing" : ""}`}>
        {dash && mounted && (
          <GridLayout
            width={width}
            layout={layout}
            gridConfig={{ cols: DASHBOARD_COLS, rowHeight: DASHBOARD_ROW_PX, margin: [12, 12], containerPadding: [0, 0] }}
            dragConfig={{ enabled: editing, handle: ".widget-drag", bounded: true }}
            resizeConfig={{ enabled: editing, handles: ["se"] }}
            onLayoutChange={onLayoutChange}
          >
            {dash.widgets.map((w) => {
              const sub = `dash:${dash.id}:${w.id}`;
              return (
                <div key={w.id}>
                  <WidgetFrame title={defaultTitle(w)} editing={editing} onSettings={() => setSettings({ widget: w, isNew: false })} onRemove={() => removeWidget(w)} actions={<WidgetActions widget={w} sub={sub} />}>
                    <WidgetBody widget={w} sub={sub} />
                  </WidgetFrame>
                </div>
              );
            })}
          </GridLayout>
        )}
        {dash && dash.widgets.length === 0 && (
          <div className="py-16 text-center text-muted">{dash.canEdit ? "Empty dashboard. Use Add widget to start." : "This dashboard has no widgets yet."}</div>
        )}
      </div>
      {settings && <WidgetSettingsDialog widget={settings.widget} isNew={settings.isNew} onSave={saveSettings} onClose={() => setSettings(null)} />}
      {sharing && dash && (
        <DashboardShareDialog
          access={dash.access}
          onSave={(access) => {
            setSharing(false);
            void save({ access });
          }}
          onClose={() => setSharing(false)}
        />
      )}
    </div>
  );
}
